import React from "react";
import { Controller } from "react-hook-form";
import { Col, FormGroup, Input, Label, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useLocalizedText } from "../../../utils/hooks/useLocalizedText";

export const PlatformPermissions = ({
  permissions,
  control,
  setValue,
  watch,
  errors,
}) => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  // Watch the system_ids value
  const system_ids = watch ? watch("system_ids") : [];

  const setValueConfig = {
    shouldValidate: true,
    shouldDirty: true,
    shouldTouch: true,
  };

  // Check if all permissions are selected
  const allSelected = permissions.every((perm) =>
    system_ids.includes(perm?.id)
  );

  // Handle individual checkbox change
  const handlePermissionChange = (permissionId, checked) => {
    const currentPermissions = system_ids || [];
    let newPermissions;

    if (checked) {
      newPermissions = [...currentPermissions, permissionId];
    } else {
      newPermissions = currentPermissions.filter((p) => p !== permissionId);
    }

    setValue("system_ids", newPermissions, setValueConfig);
  };

  // Handle "All" checkbox change
  const handleAllChange = (checked) => {
    if (checked) {
      // Select all permissions
      const allPermissionValues = permissions.map((p) => p?.id);
      setValue("system_ids", allPermissionValues, setValueConfig);
    } else {
      // Deselect all permissions
      setValue("system_ids", [], setValueConfig);
    }
  };

  return (
    <>
      {/* Hidden controller for validation */}
      <Controller
        name="system_ids"
        control={control}
        rules={{
          required: t("PERMISSION_SELECTION_REQUIRED"),
          validate: (value) => {
            if (!value || value.length === 0) {
              return t("PERMISSION_SELECTION_REQUIRED");
            }
            return true;
          },
        }}
        render={({ field }) => (
          <Input
            {...field}
            value={system_ids || []}
            className="d-none"
            multiple
            type="select"
            invalid={!!errors?.system_ids}
          />
        )}
      />

      <Label className="fw-bold required">{t("PLATFORM_PERMISSIONS")}</Label>
      {errors?.system_ids && (
        <div className="text-danger mt-1 mb-2">{errors.system_ids.message}</div>
      )}

      <Row>
        <Col xs={6} md={4}>
          <FormGroup check inline>
            <Input
              type="checkbox"
              id="all"
              checked={allSelected}
              onChange={(e) => handleAllChange(e.target.checked)}
              className={errors?.system_ids ? "border-danger" : ""}
            />
            <Label for="all">{t("ALL")}</Label>
          </FormGroup>
        </Col>

        {permissions.map((permission) => (
          <Col xs={6} md={4} key={permission?.id}>
            <FormGroup check inline>
              <Input
                type="checkbox"
                id={permission?.id}
                checked={system_ids.includes(permission?.id)}
                onChange={(e) =>
                  handlePermissionChange(permission?.id, e.target.checked)
                }
                className={errors?.system_ids ? "border-danger" : ""}
              />
              <Label for={permission?.id}>
                {getLocalizedText(permission.name_ar, permission.name_en ?? "")}
              </Label>
            </FormGroup>
          </Col>
        ))}
      </Row>
    </>
  );
};
